import { AMBIENT_PROFILES, AMBIENT_SAMPLES, AmbientProfile, COMBAT_GAINS } from '../config/audio-tuning';
import { playNoiseBurst, playTone, startNoiseLoop } from './synth';
import { SampleBank } from './sample-bank';

interface StoppableNode {
  disconnect(): void;
  stop?(): void;
}

/**
 * Per-stratum ambient bed: two-or-three detuned low drones (a slow LFO
 * breathes their combined gain so it doesn't sit perfectly static), a
 * continuous filtered noise floor, and a randomly-timed "event" one-shot
 * (drip/rattle/resonance per stratum, see `audio-tuning.ts`). Started once
 * per stratum join (`dungeon-instance-bootstrap.ts`'s room-join sequence,
 * not the per-frame loop — a stratum change tears down and remounts the
 * whole bootstrap, so there's no live "profile changed mid-session" case
 * to handle here).
 */
export class AmbientBed {
  private nodes: StoppableNode[] = [];
  private eventTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  constructor(
    private readonly context: AudioContext,
    private readonly destination: AudioNode,
    /** Optional: without one, the bed stays the oscillator drone it has always been. */
    private readonly samples?: SampleBank,
  ) {}

  start(stratumDepth: number): void {
    const profile = AMBIENT_PROFILES[stratumDepth] ?? AMBIENT_PROFILES[1];
    const recorded = AMBIENT_SAMPLES[stratumDepth] ?? AMBIENT_SAMPLES[1];

    /*
     * The recorded bed replaces the drones entirely rather than layering over
     * them — a synthesized drone under a recorded room is two rooms, and the
     * seam between them is more audible than either.
     *
     * Loaded here rather than preloaded with everything else: a bed is ~270KB
     * and a run visits one stratum, so eagerly fetching all three would spend
     * 800KB to have two of them never play. That means the first seconds of a
     * run use the synthesized bed while this decodes, and then cross over.
     */
    if (this.samples) {
      this.samples.preload([recorded.bed, ...recorded.events]);
      void this.startRecordedBed(recorded.bed);
      this.scheduleNextEvent(profile, recorded.events);
      return;
    }

    const droneBus = this.context.createGain();
    droneBus.connect(this.destination);
    this.nodes.push(droneBus);

    for (const hz of profile.droneHzs) {
      const osc = this.context.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = hz;
      const gain = this.context.createGain();
      gain.gain.value = 0.5 / profile.droneHzs.length; // multiple drones sum to a constant total, regardless of stratum's drone count
      osc.connect(gain).connect(droneBus);
      osc.start();
      this.nodes.push(osc, gain);
    }

    // Slow LFO on the combined drone gain — a subtle "breathing" so the bed
    // doesn't read as a perfectly static synth tone.
    const lfo = this.context.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.08; // ~12.5s cycle
    const lfoDepth = this.context.createGain();
    lfoDepth.gain.value = 0.25;
    lfo.connect(lfoDepth).connect(droneBus.gain);
    droneBus.gain.value = 0.75; // LFO output (±0.25) modulates around this center
    lfo.start();
    this.nodes.push(lfo, lfoDepth);

    const { source, filter, gainNode } = startNoiseLoop(this.context, this.destination, 'lowpass', profile.noiseCutoffHz, 0.06);
    this.nodes.push(source, filter, gainNode);

    this.scheduleNextEvent(profile, recorded.events);
  }

  /**
   * Poll for the decoded bed and start it looping, fading in.
   *
   * A poll rather than awaiting the load, because `SampleBank` deliberately
   * exposes readiness rather than a promise — its whole contract is "ask, and
   * fall back if the answer is no". Gives up after a few seconds; a bed that
   * has not arrived by then is not going to, and the synthesized drone is
   * already covering.
   */
  private async startRecordedBed(url: string): Promise<void> {
    for (let attempt = 0; attempt < 40 && !this.disposed; attempt++) {
      if (this.samples?.isReady(url)) break;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (this.disposed || !this.samples?.isReady(url)) return;

    const buffer = this.samples.bufferFor(url);
    if (!buffer) return;

    const gain = this.context.createGain();
    gain.gain.value = 0;
    gain.connect(this.destination);
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    source.start();

    const now = this.context.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(COMBAT_GAINS.AMBIENT_BED, now + 2);

    // `nodes` is the single owner for teardown — stop() walks it.
    this.nodes.push(source, gain);
  }

  private scheduleNextEvent(profile: AmbientProfile, recordedEvents?: readonly string[]): void {
    const [minMs, maxMs] = profile.eventIntervalRangeMs;
    const delayMs = minMs + Math.random() * (maxMs - minMs);
    this.eventTimeoutHandle = setTimeout(() => {
      if (this.disposed) return;
      const pan = (Math.random() - 0.5) * 1.2;
      if (!recordedEvents?.length
        || !this.samples?.playVariant(recordedEvents, { gain: COMBAT_GAINS.AMBIENT_EVENT, pan })) {
        this.playEvent(profile.eventType);
      }
      this.scheduleNextEvent(profile, recordedEvents);
    }, delayMs);
  }

  private playEvent(eventType: AmbientProfile['eventType']): void {
    switch (eventType) {
      case 'drip':
        playNoiseBurst(this.context, this.destination, {
          durationS: 0.04,
          filterType: 'bandpass',
          filterFreq: 3200 + Math.random() * 800,
          filterQ: 6,
          envelope: { attackS: 0.001, decayS: 0.06, peakGain: 0.15 },
          pan: (Math.random() - 0.5) * 1.2,
        });
        break;
      case 'rattle': {
        const clicks = 2 + Math.floor(Math.random() * 3);
        for (let i = 0; i < clicks; i++) {
          playNoiseBurst(this.context, this.destination, {
            durationS: 0.02,
            filterType: 'bandpass',
            filterFreq: 1400 + Math.random() * 600,
            filterQ: 8,
            envelope: { attackS: 0.001, decayS: 0.03, peakGain: 0.12 },
            delayS: i * 0.05,
            pan: (Math.random() - 0.5) * 1.2,
          });
        }
        break;
      }
      case 'resonance':
        playTone(this.context, this.destination, {
          freq: 130 + Math.random() * 40,
          type: 'sine',
          detune: (Math.random() - 0.5) * 20,
          envelope: { attackS: 0.4, decayS: 1.8, peakGain: 0.1 },
          pan: (Math.random() - 0.5) * 1.2,
        });
        break;
    }
  }

  stop(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.eventTimeoutHandle !== null) clearTimeout(this.eventTimeoutHandle);
    for (const node of this.nodes) {
      node.stop?.();
      node.disconnect();
    }
    this.nodes = [];
  }
}
